import type { AnyFieldApi, ReactFormExtendedApi } from '@tanstack/react-form';
import type { InputAreaProps } from '@tuic/web-compositions/shared/InputArea';
import type { InputFieldProps } from '@tuic/web-compositions/shared/InputField';
import type { SelectFieldProps } from '@tuic/web-compositions/shared/SelectField';
import type { ChangeEvent } from 'react';

/**
 * A loosely-typed handle to a TanStack form configured with form data of shape `D`.
 *
 * Each validator slot is `any` because TanStack's generic signature requires the exact
 * validator type configured in `useForm({ validators: ... })` at that slot. Picking
 * `undefined` would reject forms that *do* configure a validator (e.g. ManifestForm's
 * `onDynamic: schema`); `unknown` does not satisfy the underlying constraint
 * (`T extends undefined | FormValidateOrFn<D>`). `any` mirrors what TanStack's own
 * `AnyFormApi` uses internally for the same reason.
 *
 * Used by section components that only need the form handle as a generic `D`-shaped
 * container — see `ManifestForm.tsx`, where the concrete form is cast via
 * `form as unknown as SimpleFormApi<ManifestFormValues>` before being passed down.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SimpleFormApi<D> = ReactFormExtendedApi<
  D,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any,
  any
>;

export function formInputField(
  field: AnyFieldApi,
): Pick<InputFieldProps, 'onBlur' | 'onChange' | 'value' | 'name' | 'error'> {
  return {
    onBlur: field.handleBlur,
    onChange: e => field.handleChange(e.target.value),
    value: field.state.value as HTMLInputElement['value'],
    name: field.name,
    error: field.state.meta.errors.map(e => e.message).join(','),
  };
}

export function formInputArea(
  field: AnyFieldApi,
): Pick<InputAreaProps, 'onBlur' | 'onChange' | 'value' | 'name' | 'error'> {
  return {
    onBlur: field.handleBlur,
    onChange: e => field.handleChange(e.target.value),
    value: field.state.value as HTMLTextAreaElement['value'],
    name: field.name,
    error: field.state.meta.errors.map(e => e.message).join(','),
  };
}

export function formSelectField(
  field: AnyFieldApi,
): Pick<
  SelectFieldProps,
  'onChange' | 'defaultValue' | 'value' | 'name' | 'error'
> {
  return {
    onChange: (e: ChangeEvent<HTMLSelectElement>) =>
      field.handleChange(e.target.value),
    defaultValue: field.state.value,
    value: field.state.value as HTMLInputElement['value'],
    name: field.name,
    error: field.state.meta.errors.map(e => e.message).join(','),
  };
}

export function formCheckboxField(field: AnyFieldApi) {
  return {
    checked: field.state.value as boolean,
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      field.handleChange(e.target.checked),
    error: field.state.meta.errors.map(e => e.message).join(','),
  };
}
